import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const linkClass = isScrolled
    ? 'text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer'
    : 'text-sm text-background-50/90 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer';

  const btnOutlineClass = isScrolled
    ? 'inline-flex items-center px-4 py-2 rounded-full border border-foreground-300/60 text-foreground-900 text-sm font-medium hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer'
    : 'inline-flex items-center px-4 py-2 rounded-full border border-background-50/60 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer';

  const btnSolidClass = isScrolled
    ? 'inline-flex items-center px-4 py-2 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer'
    : 'inline-flex items-center px-4 py-2 rounded-full bg-background-50 text-foreground-900 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer';

  const categoryBtnClass = isScrolled
    ? 'flex items-center gap-1.5 px-4 py-2 rounded-full border border-foreground-300/60 text-sm text-foreground-800 hover:border-foreground-500 transition-colors whitespace-nowrap cursor-pointer bg-transparent'
    : 'flex items-center gap-1.5 px-4 py-2 rounded-full border border-background-50/50 text-sm text-background-50 hover:border-background-50 transition-colors whitespace-nowrap cursor-pointer bg-transparent';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <span className={`font-heading text-xl md:text-2xl font-bold tracking-tight transition-colors duration-300 ${
              isScrolled ? 'text-foreground-900' : 'text-background-50'
            }`}>
              LitBuy <span className={isScrolled ? 'text-primary-600' : 'text-background-50/80'}>Spreadsheets</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            <Link to="/" className={linkClass}>
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={categoryBtnClass}
              >
                Categories
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-60 bg-background-50 rounded-xl border border-background-200/80 shadow-lg py-3 max-h-96 overflow-y-auto z-50">
                  <div className="px-4 pb-2 mb-2 border-b border-background-100">
                    <span className="text-xs text-foreground-400 font-medium uppercase tracking-wider">Product Categories</span>
                  </div>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                        <i className={`${cat.type === 'search' ? 'ri-search-line' : 'ri-store-2-line'} text-xs`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to="/article/shopping-guide-litbuy-spreadsheet" className={linkClass}>
              Shopping Guide
            </Link>

            <Link to="/article/what-is-litbuy-spreadsheet" className={linkClass}>
              Guides
            </Link>

            <a href="/#safety" className={linkClass}>
              Safety
            </a>

            <div className="w-px h-5 bg-background-300/40 mx-1 hidden lg:block" />

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={btnSolidClass}
            >
              LitBuy Spreadsheet
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={btnOutlineClass}
            >
              Buy It Now
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`text-xl transition-colors duration-300 ${isScrolled ? 'text-foreground-900' : 'text-background-50'} ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-5 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-home-4-line"></i>
              </span>
              Home
            </Link>

            <Link
              to="/article/shopping-guide-litbuy-spreadsheet"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                <i className="ri-guide-line"></i>
              </span>
              Shopping Guide
            </Link>

            <Link
              to="/article/what-is-litbuy-spreadsheet"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-book-open-line"></i>
              </span>
              Guides
            </Link>

            <a
              href="/#safety"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                <i className="ri-shield-check-line"></i>
              </span>
              Safety
            </a>

            <div className="mt-2 pt-3 border-t border-background-200/60">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-3 py-3 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center text-secondary-500">
                    <i className="ri-grid-line"></i>
                  </span>
                  Categories ({categories.length})
                </span>
                <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-foreground-500"></i>
                </span>
              </button>

              {mobileCatOpen && (
                <div className="mt-1 ml-3 pl-5 border-l-2 border-background-200/60 space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                        <i className={`${cat.type === 'search' ? 'ri-search-line' : 'ri-arrow-right-up-line'} text-xs`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-3 pt-3 border-t border-background-200/60 flex flex-col gap-2.5 px-1">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line text-xs"></i>
                </span>
                LitBuy Spreadsheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-foreground-300/60 text-foreground-900 text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shopping-bag-3-line text-sm"></i>
                </span>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}